/**
 * File Service
 * 
 * Handles file uploads, KML generation, and file parsing operations.
 */

import { Layer, Polygon, Point } from '../../context/context';
import { AgentService, UploadFileResult } from './agentService';

/**
 * Generate KML from a layer
 * @param layer - The layer to convert to KML
 * @returns KML string
 */
export const generateKMLFromLayer = (layer: Layer): string => {
  const esc = (str: string) =>
    str.replace(/[<>&'"]/g, (c) =>
      c === '<' ? '&lt;'
      : c === '>' ? '&gt;'
      : c === '&' ? '&amp;'
      : c === '"' ? '&quot;'
      : c === "'" ? '&apos;'
      : c
    );

  let kml = `<?xml version="1.0" encoding="UTF-8"?>\n`;
  kml += `<kml xmlns="http://www.opengis.net/kml/2.2">\n`;
  kml += `<Document>\n`;
  kml += `<name>${esc(layer.layerName)}</name>\n`;
  kml += `<description>Geofence layer exported from Atlas</description>\n`;

  // Add polygons and points as placemarks
  layer.shapes.forEach(shape => {
    if (shape.type === 'point') {
      const point = shape as Point;
      const name = esc(point.name || 'Point');
      const coords = `${point.coordinates[1]},${point.coordinates[0]},0`;
      kml += `<Placemark>\n`;
      kml += `  <name>${name}</name>\n`;
      kml += `  <Point>\n`;
      kml += `    <coordinates>${coords}</coordinates>\n`;
      kml += `  </Point>\n`;
      kml += `</Placemark>\n`;
    } else if (shape.type === 'polygon') {
      const polygon = shape as Polygon;
      const name = esc(polygon.name || 'Polygon');
      // Only use the first ring (outer boundary) for simplicity
      if (polygon.coordinates && polygon.coordinates[0]) {
        const coords = polygon.coordinates[0]
          .map(([lat, lng]: [number, number]) => `${lng},${lat},0`)
          .join(' ');
        kml += `<Placemark>\n`;
        kml += `  <name>${name}</name>\n`;
        kml += `  <Polygon>\n`;
        kml += `    <outerBoundaryIs>\n`;
        kml += `      <LinearRing>\n`;
        kml += `        <coordinates>${coords}</coordinates>\n`;
        kml += `      </LinearRing>\n`;
        kml += `    </outerBoundaryIs>\n`;
        kml += `  </Polygon>\n`;
        kml += `</Placemark>\n`;
      }
    }
  });

  kml += `</Document>\n`;
  kml += `</kml>`;
  return kml;
};

/**
 * Determine file type from filename
 * @param fileName - The file name
 * @returns File type ('kml', 'csv', 'xlsx', or 'unknown')
 */
export const getFileType = (fileName: string): 'kml' | 'csv' | 'xlsx' | 'unknown' => {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.kml')) return 'kml';
  if (lower.endsWith('.csv')) return 'csv';
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return 'xlsx';
  return 'unknown';
};

/**
 * Upload a file using AgentService
 * @param file - The file to upload
 * @param agentService - The agent service instance
 * @returns Upload result with file ID and parsed data
 */
export const uploadFile = async (
  file: File,
  agentService: AgentService
): Promise<UploadFileResult> => {
  return await agentService.uploadFile(file);
};

/**
 * Get file data by ID
 * @param fileId - The file ID
 * @param agentService - The agent service instance
 * @returns File data including parsed content
 */
export const getFile = async (
  fileId: string,
  agentService: AgentService
): Promise<UploadFileResult> => {
  return await agentService.getFile(fileId);
};

/**
 * Parse KML file data to extract hubs/points
 * @param fileData - The file data from upload/getFile
 * @returns Array of hub objects with name, lat, lng
 */
export const parseKMLHubs = (fileData: UploadFileResult): Array<{ name: string; lat: number; lng: number }> => {
  if (!fileData.parsed_data?.points) {
    return [];
  }
  
  return fileData.parsed_data.points.map((p: any) => ({
    name: p.name || `Point ${p.lat}, ${p.lng}`,
    lat: p.lat,
    lng: p.lng,
  }));
};

/**
 * Parse Excel/CSV file data to extract column information
 * @param fileData - The file data from upload/getFile
 * @returns Object with columns and validation info
 */
export const parseExcelColumns = (fileData: UploadFileResult): {
  columns: string[];
  requiredColumnsPresent: boolean;
  missingColumns?: string[];
} => {
  if (!fileData.parsed_data?.columns) {
    return {
      columns: [],
      requiredColumnsPresent: false,
    };
  }
  
  return {
    columns: fileData.parsed_data.columns,
    requiredColumnsPresent: fileData.parsed_data.required_columns_present || false,
    missingColumns: fileData.parsed_data.missing_columns,
  };
};

